/* eslint-disable no-restricted-imports */
/**
 * Utils to parse the date
 */

import { isMatch, isValid, parse } from 'date-fns';

import { getDateFeatureConfig } from './DateConfigType';
import { DateFormat } from './dateFormats';
import { addDefaultTimezoneToUTCDate } from './timezone';

type DateInputType = undefined | string | null | number;

function getUndefinedIfNotValid(date: Date): Date | undefined {
    if (!isValid(date)) {
        return undefined;
    }

    return date;
}

function isDateWithoutTimezone(date: string): boolean {
    return !!date.match(/^\d{4}-\d{2}-\d{2}$/);
}

/**
 * Parses the date string to date object.
 * If given string is not valid date, undefined is returned.
 *
 * @param date - date to parse
 * @param format - format to use
 * @param strict - use strict parsing
 * @return parsed date object or undefined
 */
export function parseDate(
    date: DateInputType,
    format?: DateFormat,
    strict?: boolean,
): Date | undefined {
    // @ts-expect-error - date is not typed as any
    // noinspection SuspiciousTypeOfGuard
    if (date instanceof Date) {
        throw new Error('Parsing "Date" instance is invalid operation');
    }
    if (strict && typeof date === 'string' && format && !isMatch(date, format)) {
        return undefined;
    }
    if (date === undefined || date === null) {
        return new Date();
    }
    if (typeof date === 'number') {
        return getUndefinedIfNotValid(new Date(date));
    }

    if (isDateWithoutTimezone(date)) {
        return addDefaultTimezoneToUTCDate(new Date(date));
    }

    const defaultFormat =
        getDateFeatureConfig((f) => f?.base?.defaultDateFormat) ?? DateFormat.DATE_MONTH_YEAR_DOTS;

    if (!format && defaultFormat && isMatch(date, defaultFormat)) {
        return parse(date, defaultFormat, new Date(), { useAdditionalDayOfYearTokens: true });
    }

    if (!format) {
        return getUndefinedIfNotValid(new Date(date));
    }

    const parsedDate = parse(date, format, new Date(), { useAdditionalDayOfYearTokens: true });

    return getUndefinedIfNotValid(parsedDate);
}
